import { Button, Checkbox, Input, Modal, Space, Typography } from 'antd';
import React, { FC, useState, useEffect } from 'react';
import { ViewLabel } from 'models/view-labels';
import {
  ScrollableList,
  SelectButtonsRow,
  InfoTextRow,
  CheckboxRow,
  CommentBox,
} from './RejectModal.styled';

const { Text } = Typography;

interface IProps {
  isModalOpen: boolean;
  handleCancel: () => void;
  handleOk: (rejectedViewLabels: string[], comment: string) => void;
  segmentations: ViewLabel[]; // Changed from KeypointCollectionLabel[] to ViewLabel[]
  initialRejectedIds?: string[];
}

const RejectModal: FC<IProps> = props => {
  const {
    isModalOpen,
    handleCancel,
    handleOk,
    segmentations: viewLabels,
    initialRejectedIds = [],
  } = props;
  const [comment, setComment] = useState('');
  const [rejectedViewLabels, setRejectedViewLabels] = useState<Set<string>>(
    new Set(initialRejectedIds)
  );

  useEffect(() => {
    if (isModalOpen) {
      // default to selecting all view labels for faster technician reviews
      const allIds = viewLabels.map(vl => vl.uuid);
      setRejectedViewLabels(new Set(allIds));
      setComment('');
    }
  }, [isModalOpen, initialRejectedIds]);

  const handleCheckboxChange = (viewLabelId: string, checked: boolean) => {
    setRejectedViewLabels(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(viewLabelId);
      } else {
        newSet.delete(viewLabelId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const allIds = viewLabels.map(vl => vl.uuid);
    setRejectedViewLabels(new Set(allIds));
  };

  const handleSelectNone = () => {
    setRejectedViewLabels(new Set());
  };

  return (
    <Modal
      title="Select View Labels to Reject"
      open={isModalOpen}
      onCancel={handleCancel}
      footer={null}
      width={600}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <SelectButtonsRow>
            <Button size="small" onClick={handleSelectAll} style={{ marginRight: 8 }}>
              Select All
            </Button>
            <Button size="small" onClick={handleSelectNone}>
              Select None
            </Button>
          </SelectButtonsRow>
          <InfoTextRow>
            <Text type="secondary">Unselected view labels will be automatically approved.</Text>
          </InfoTextRow>
        </div>

        <ScrollableList>
          {viewLabels.map(viewLabel => {
            const isRejected = rejectedViewLabels.has(viewLabel.uuid);
            return (
              <CheckboxRow key={viewLabel.uuid}>
                <Checkbox
                  checked={isRejected}
                  onChange={e => handleCheckboxChange(viewLabel.uuid, e.target.checked)}
                >
                  <Space direction="vertical" size={0}>
                    <Text strong>{viewLabel.viewClass?.view}</Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {viewLabel.uuid}
                      {isRejected && ' (Currently Rejected)'}
                    </Text>
                  </Space>
                </Checkbox>
              </CheckboxRow>
            );
          })}
        </ScrollableList>

        <CommentBox>
          <Text strong>Comment (optional):</Text>
          <Input.TextArea
            showCount
            rows={3}
            value={comment}
            onChange={e => setComment(e.target.value)}
            maxLength={255}
            placeholder="Add a comment for the rejected view labels..."
            style={{ marginTop: 8, resize: 'none' }}
          />
        </CommentBox>

        <Space style={{ width: '100%', justifyContent: 'space-between' }} size={12}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            type="primary"
            onClick={() => handleOk(Array.from(rejectedViewLabels), comment)}
            disabled={rejectedViewLabels.size === 0}
          >
            Reject ({rejectedViewLabels.size} selected)
          </Button>
        </Space>
      </Space>
    </Modal>
  );
};
export default RejectModal;
